import { Link } from "react-router-dom";
import { ArrowUpRight, Package, Truck, Wrench, ShieldCheck, Globe2, Factory, MessagesSquare } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import LiveFromChinaGallery from "../components/LiveFromChinaGallery";
import HeroCarousel from "../components/HeroCarousel";
import SourcingJourney from "../components/SourcingJourney";
import WhoWeAre from "../components/WhoWeAre";

const IMG_FURNITURE = "https://images.pexels.com/photos/6492402/pexels-photo-6492402.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_HARDWARE = "/brand/ikel-hinge-promo.jpg";
const IMG_SHIP = "https://images.pexels.com/photos/12530455/pexels-photo-12530455.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const trustIcons = [ShieldCheck, Factory, Truck, MessagesSquare];

export default function Home() {
  const { t } = useLang();

  return (
    <div data-testid="home-page">
      {/* HERO CAROUSEL */}
      <HeroCarousel />

      {/* TRIO SEGMENTS */}
      <section id="divisions" className="py-24 md:py-32 bg-[#F5F5F7]">
        <div className="pgb-container">
          <div className="max-w-3xl mb-16">
            <span className="eyebrow">{t.trio.eyebrow}</span>
            <h2 className="mt-4 text-3xl md:text-5xl font-black tracking-tighter text-[#0B1B29]">
              {t.trio.title}
            </h2>
            <div className="mt-6 inline-flex items-center gap-2 border border-[#0B1B29]/15 bg-white px-4 py-2">
              <span className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full" />
              <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#0B1B29]">
                {t.common.readyBadge}
              </span>
            </div>
            <p className="mt-3 text-sm text-[#536878] max-w-xl leading-relaxed">
              {t.common.readyCatalogue}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <TrioCard
              testId="trio-ikel"
              tag={t.trio.ikel.tag}
              title={t.trio.ikel.title}
              desc={t.trio.ikel.desc}
              cta={t.trio.ikel.cta}
              image={IMG_HARDWARE}
              to="/ikel-mt"
              icon={<Wrench size={18} />}
            />
            <TrioCard
              testId="trio-furniture"
              tag={t.trio.furniture.tag}
              title={t.trio.furniture.title}
              desc={t.trio.furniture.desc}
              cta={t.trio.furniture.cta}
              image={IMG_FURNITURE}
              to="/furniture"
              icon={<Package size={18} />}
            />
            <TrioCard
              testId="trio-logistics"
              tag={t.trio.logistics.tag}
              title={t.trio.logistics.title}
              desc={t.trio.logistics.desc}
              cta={t.trio.logistics.cta}
              image={IMG_SHIP}
              to="/logistics"
              icon={<Truck size={18} />}
            />
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-20 md:py-28 bg-[#0B1B29] text-white">
        <div className="pgb-container">
          <span className="eyebrow text-[#93C5FD]">{t.stats.eyebrow}</span>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
            {t.stats.items.map((item, idx) => (
              <div
                key={item.label}
                className="border-l border-white/15 pl-6 py-2"
                data-testid={`stat-item-${idx}`}
              >
                <div className="text-4xl md:text-6xl font-black tracking-tighter" style={{ fontFamily: "Chivo" }}>
                  {item.value}
                </div>
                <div className="mt-2 text-xs tracking-[0.18em] uppercase text-white/60">
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <WhoWeAre />
      <section className="py-24 md:py-32 bg-white">
        <div className="pgb-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="eyebrow">{t.trust.eyebrow}</span>
            <h2 className="mt-4 text-3xl md:text-5xl font-black tracking-tighter text-[#0B1B29]">
              {t.trust.title}
            </h2>
            <div className="mt-8 flex items-center gap-2 text-sm text-[#536878]">
              <Globe2 size={16} className="text-[#1D4ED8]" />
              <span className="font-mono tracking-wider uppercase text-xs">Foshan  ·  Guangzhou  ·  Baku</span>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E2E8F0]">
            {t.trust.items.map((item, idx) => {
              const Icon = trustIcons[idx % trustIcons.length];
              return (
                <div
                  key={idx}
                  className="bg-white p-7 hover:bg-[#F5F5F7] transition-colors"
                  data-testid={`trust-item-${idx}`}
                >
                  <Icon size={22} className="text-[#1D4ED8]" />
                  <h3 className="mt-4 text-lg font-bold text-[#0B1B29]">{item.title}</h3>
                  <p className="mt-2 text-sm text-[#536878] leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-[#1D4ED8] text-white py-20">
        <div className="pgb-container flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <span className="eyebrow text-white/70">{t.common.getInTouch}</span>
            <h3 className="mt-3 text-2xl md:text-4xl font-black tracking-tight">
              {t.hero.ctaPrimary} — {t.hero.shortTagline}
            </h3>
          </div>
          <Link to="/quote" className="btn-primary bg-white text-[#0B1B29] hover:bg-[#0B1B29] hover:text-white" data-testid="home-bottom-cta">
            {t.common.requestQuote} <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>

      <LiveFromChinaGallery />
      <SourcingJourney />
    </div>
  );
}

function TrioCard({ tag, title, desc, cta, image, to, icon, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="pgb-card group flex flex-col bg-white"
    >
      <div className="aspect-[4/3] overflow-hidden bg-[#0B1B29]">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
      </div>
      <div className="p-7 flex flex-col gap-4 flex-1">
        <div className="flex items-center gap-2 text-[#1D4ED8]">
          {icon}
          <span className="eyebrow">{tag}</span>
        </div>
        <h3 className="text-2xl md:text-3xl font-black tracking-tight text-[#0B1B29]">
          {title}
        </h3>
        <p className="text-sm text-[#536878] leading-relaxed flex-1">{desc}</p>
        <span className="mt-2 inline-flex items-center gap-2 text-sm font-semibold tracking-wider uppercase text-[#0B1B29] group-hover:text-[#1D4ED8] transition-colors">
          {cta} <ArrowUpRight size={16} />
        </span>
      </div>
    </Link>
  );
}
